import React, { useMemo, useState } from 'react';
import { Alert, Button, Card, Input, List, Space, Tag, Typography, message } from 'antd';
import { useMutation } from '@apollo/client';
import { ADD_BLACKLIST_ADDRESSES } from './graphql/addBlacklistAddresses';

const { TextArea } = Input;
const { Title, Paragraph, Text } = Typography;

interface BlacklistProcessResult {
    address: string;
    success: boolean;
    message: string;
}

interface AddressBlacklistMutationResult {
    total: number;
    successCount: number;
    failureCount: number;
    results: BlacklistProcessResult[];
}

const AddressBlacklistTestPage: React.FC = () => {
    const [rawAddresses, setRawAddresses] = useState('');
    const [result, setResult] = useState<AddressBlacklistMutationResult | null>(null);

    const [addBlacklistAddresses, { loading }] = useMutation<
        { addBlacklistAddresses: AddressBlacklistMutationResult },
        { addresses: string }
    >(ADD_BLACKLIST_ADDRESSES);

    const handleSubmit = async () => {
        if (!rawAddresses.trim()) {
            message.warning('请输入需要加入黑名单的地址');
            return;
        }

        try {
            setResult(null)
            const { data } = await addBlacklistAddresses({
                variables: { addresses: rawAddresses },
            });
            if (data?.addBlacklistAddresses) {
                setResult(data.addBlacklistAddresses);
                message.success('处理完成');
            }
        } catch (error: any) {
            console.error(error);
            message.error(error?.message ?? '提交失败，请稍后重试');
        }
    };

    const failureResults = useMemo(
        () => result?.results.filter((item) => !item.success) ?? [],
        [result]
    );

    return (
        <div className="p-6 max-w-4xl mx-auto">
            <Card>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <div>
                        <Title level={2}>地址黑名单导入工具</Title>
                        <Paragraph type="secondary">
                            将多个地址按行输入，提交后会逐条调用后台接口写入黑名单。
                        </Paragraph>
                    </div>

                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Text strong>地址列表（每行一个地址）</Text>
                        <TextArea
                            rows={8}
                            value={rawAddresses}
                            onChange={(event) => setRawAddresses(event.target.value)}
                            placeholder="示例：&#10;123 Main Street, Los Angeles, CA 90001&#10;456 Market St, San Francisco, CA 94103"
                        />
                        <Button type="primary" loading={loading} onClick={handleSubmit}>
                            提交
                        </Button>
                    </Space>

                    {result && (
                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            <Alert
                                message={
                                    <Space direction="vertical" size={0}>
                                        <Text>总计：{result.total}</Text>
                                        <Text type="success">成功：{result.successCount}</Text>
                                        <Text type="danger">失败：{result.failureCount}</Text>
                                    </Space>
                                }
                                type="info"
                                showIcon
                            />

                            <List
                                header="处理明细"
                                dataSource={result.results}
                                renderItem={(item) => (
                                    <List.Item>
                                        <Space direction="vertical">
                                            <Space size="middle">
                                                <Tag color={item.success ? 'green' : 'red'}>
                                                    {item.success ? '成功' : '失败'}
                                                </Tag>
                                                <Text>{item.address}</Text>
                                            </Space>
                                            <Text type={item.success ? 'secondary' : 'danger'}>
                                                {item.message}
                                            </Text>
                                        </Space>
                                    </List.Item>
                                )}
                            />

                            {failureResults.length > 0 && (
                                <Alert
                                    type="warning"
                                    message="失败详情"
                                    description={
                                        <List
                                            dataSource={failureResults}
                                            renderItem={(item) => (
                                                <List.Item>
                                                    <Space direction="vertical">
                                                        <Text strong>{item.address}</Text>
                                                        <Text type="danger">{item.message}</Text>
                                                    </Space>
                                                </List.Item>
                                            )}
                                        />
                                    }
                                    showIcon
                                />
                            )}
                        </Space>
                    )}
                </Space>
            </Card>
        </div>
    );
};

export default AddressBlacklistTestPage;

